import { HStack, PageContainer, PageHeader, Spread } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { Filter } from '@/components/DataTable/FilterControls'
import { ExportCsvButton } from '@/components/ExportCsv/ExportCsvButton'
import { useCrtVersionSelectionSync } from '@/hooks/crt/useCrtVersionSelectionSync'
import useBrandFilter from '@/hooks/filters/useBrandFilter'
import useMarkedFilter from '@/hooks/filters/useMarkedFilter'
import useMaterialGroupFilter from '@/hooks/filters/useMaterialGroupFilter'
import usePackContentSizeFilter from '@/hooks/filters/usePackContentSizeFilter'
import useProductCategoryFilter from '@/hooks/filters/useProductCategoryFilter'
import useProductTypeFilter from '@/hooks/filters/useProductTypeFilter'
import useProvinceFilter from '@/hooks/filters/useProvinceFilter'
import useVariantFilter from '@/hooks/filters/useVariantFilter'
import {
  brandVariantsExportQueryOptions,
  brandVariantsSearchSchema,
  useBrandVariants,
} from '@/hooks/queries/crt/useBrandVariants'
import { useTablePagination } from '@/hooks/useTablePagination'
import { getClearedFilters, MultipleToggleListControl } from '@/lib/utils/filters'
import { queryClient } from '@/main'
import { CHANNEL_VALUES, INDETERMINATE } from '@repo/constants'
import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BrandVariantsDataTable } from './-components/BrandVariantsDataTable'

export const Route = createFileRoute('/_auth/_modules/crt/materials/brand-variants/')({
  component: BrandVariantsPage,
  validateSearch: (search) => brandVariantsSearchSchema.parse(search),
})

function BrandVariantsPage() {
  const { t } = useTranslation('crt')
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { pageSize, onPageSizeChange } = useTablePagination({ navigate, search })

  // The header's period: the page lists the brand variants that period's price list copy prices.
  const { crtPeriod } = useCrtVersionSelectionSync({
    search,
    navigate: (search) => navigate({ search }),
    fields: [],
  })

  const [keyword, setKeyword] = useState<string>(search.keyword ?? '')

  const { provinceFilter: provinceFilter, province: provinces } = useProvinceFilter(
    'code',
    search.provinces,
    {
      name: 'provinces',
      title: t('brandVariants.filters.provinces'),
    },
  )

  const { brandFilter, brands } = useBrandFilter(search.brand, { isOrderable: true, crtPeriod })
  const { variantFilter, variants } = useVariantFilter(search.variant, {
    isOrderable: true,
    crtPeriod,
  })
  const { productCategoryFilter, productCategories } = useProductCategoryFilter(
    search.productCategory,
  )
  const { materialGroupFilter, materialGroups } = useMaterialGroupFilter(search.materialGroupCode)
  const { packContentSizeFilter, packContentSizes } = usePackContentSizeFilter(
    search.packContentSize,
  )
  const { productTypeFilter, productTypes } = useProductTypeFilter(search.productType)
  const { markedFilter, marked, setMarked } = useMarkedFilter(search.marked)
  const [channels, setChannels] = useState<string[]>(search.channels ?? [])

  const selectedFilters = useMemo(() => {
    return {
      productCategory: productCategories?.length > 0 ? productCategories : undefined,
      materialGroupCode: materialGroups?.length > 0 ? materialGroups : undefined,
      brand: brands?.length > 0 ? brands : undefined,
      variant: variants?.length > 0 ? variants : undefined,
      packContentSize: packContentSizes?.length > 0 ? packContentSizes : undefined,
      productType: productTypes?.length > 0 ? productTypes : undefined,
      provinces: provinces?.length > 0 ? provinces : undefined,
      marked: marked !== INDETERMINATE ? marked : undefined,
      channels: channels?.length > 0 ? channels : undefined,
    }
  }, [
    productCategories,
    materialGroups,
    brands,
    variants,
    packContentSizes,
    productTypes,
    provinces,
    marked,
    channels,
  ])

  const showResults = () => {
    navigate({
      search: {
        ...search,
        ...selectedFilters,
        keyword: keyword || undefined,
        page: 1,
      },
    })
  }

  const resetAllFilters = () => {
    setKeyword('')
    setMarked(INDETERMINATE)
    const clearedFilters = getClearedFilters(selectedFilters)

    navigate({
      search: {
        ...search,
        ...clearedFilters,
        keyword: undefined,
        page: 1,
      },
    })
  }

  const filters = [
    productCategoryFilter,
    materialGroupFilter,
    brandFilter,
    variantFilter,
    packContentSizeFilter,
    productTypeFilter,
    provinceFilter,
    markedFilter,
    {
      control: MultipleToggleListControl,
      name: 'channels',
      title: t('brandVariants.filters.channel'),
      options: CHANNEL_VALUES.map((value) => ({
        value,
        label: value,
      })),
      value: channels,
      onChange: setChannels,
    },
  ]

  const queryOptions = {
    crtPeriod,
    keyword: search.keyword,
    productCategory: search.productCategory,
    materialGroupCode: search.materialGroupCode,
    brand: search.brand,
    variant: search.variant,
    packContentSize: search.packContentSize,
    productType: search.productType,
    channels: search.channels,
    provinces: search.provinces,
    marked: search.marked,
    page: search.page ?? 1,
    pageSize,
    sortBy: search.sort?.id,
    sortOrder: (search.sort?.desc ? 'desc' : 'asc') as 'desc' | 'asc',
  }

  const brandVariantsQuery = useBrandVariants(queryOptions)

  const data = brandVariantsQuery.data?.rows ?? []
  const rowCount = brandVariantsQuery.data?.rowCount ?? 0

  const getCsvExport = () =>
    queryClient.ensureQueryData(brandVariantsExportQueryOptions({ ...search, crtPeriod }))

  return (
    <PageContainer>
      <Spread>
        <PageHeader>{t('brandVariants.pageHeader')}</PageHeader>
        <HStack gap="sm">
          <ExportCsvButton
            getCsvExport={getCsvExport}
            rowCount={rowCount}
            label={t('export.viewData', { ns: 'common' })}
          />
        </HStack>
      </Spread>
      <Card>
        <CardContent className="pt-6">
          <BrandVariantsDataTable
            filters={filters as Filter<unknown>[]}
            search={{
              className: 'w-[30rem]',
              value: keyword,
              setValue: setKeyword,
              placeholder: t('brandVariants.searchPlaceholder'),
            }}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            data={data}
            rowCount={rowCount}
            isPending={brandVariantsQuery.isPending}
            error={brandVariantsQuery.error}
            onShowResults={showResults}
            onResetAllFilters={resetAllFilters}
          />
        </CardContent>
      </Card>
    </PageContainer>
  )
}
